import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { TransitionProvider } from './lib/transition';
import Landing from './components/screens/Landing';
import NoSeat from './components/screens/NoSeat';
import Chrome from './components/chrome/Chrome';
import Chamber from './components/chrome/Chamber';
import MobileNotice from './components/chrome/MobileNotice';
import PoliceNotice from './components/chrome/PoliceNotice';
import type { ReactNode } from 'react';
import { useWorldScene } from './world/WorldStage';
import { canLoadImages, useWorldMeta } from './world/assets';
import { roomForPath } from './world/rooms';
import './components/screens/landing.css';

// Every screen the landing's first paint does not need is its own chunk, so a
// phone parses the landing and the shell only. The Suspense fallback draws
// nothing of its own: the room is drawn by the world stage, not by the screens.
const loadAbout = () => import('./components/screens/About');
const loadTheCourt = () => import('./components/screens/TheCourt');
const loadGuide = () => import('./components/screens/Guide');
const loadTerms = () => import('./components/screens/Terms');
const loadSettings = () => import('./components/screens/Settings');
const loadCasePicker = () => import('./components/screens/CasePicker');
const loadBriefs = () => import('./components/screens/Briefs');
const loadArgument = () => import('./components/screens/Argument');
const loadSummary = () => import('./components/screens/Summary');
const loadPublicShare = () => import('./components/screens/PublicShare');
const loadAchievements = () => import('./components/screens/Achievements');
const loadRecord = () => import('./components/screens/Record');
const loadLeaderboard = () => import('./components/screens/Leaderboard');
const About = lazy(loadAbout);
const TheCourt = lazy(loadTheCourt);
const Guide = lazy(loadGuide);
const Terms = lazy(loadTerms);
const Settings = lazy(loadSettings);
const CasePicker = lazy(loadCasePicker);
const Briefs = lazy(loadBriefs);
const Argument = lazy(loadArgument);
const Summary = lazy(loadSummary);
const PublicShare = lazy(loadPublicShare);
const Achievements = lazy(loadAchievements);
const Record = lazy(loadRecord);
const Leaderboard = lazy(loadLeaderboard);
const NotFound = lazy(() => import('./components/chrome/NotFound'));

/**
 * The chunk for the page being opened, fetched now, while the session opens,
 * instead of after it: a deep link (/court, /guide...) used to wait for
 * ip-restore and anon and only then ask for its own code, which was most of
 * its largest-paint time on a slow phone (Lighthouse mobile, 2026-10-09).
 * The dynamic import is cached, so the lazy screen gets the same module.
 */
export function chunkForPath(pathname: string): (() => Promise<unknown>) | null {
  const exact: Record<string, () => Promise<unknown>> = {
    '/about': loadAbout, '/court': loadTheCourt, '/guide': loadGuide, '/terms': loadTerms,
    '/settings': loadSettings, '/home': loadCasePicker, '/achievements': loadAchievements,
    '/record': loadRecord, '/leaderboard': loadLeaderboard,
  };
  if (exact[pathname]) return exact[pathname];
  if (/^\/case\/[^/]+\/brief$/.test(pathname)) return loadBriefs;
  if (/^\/play\/[^/]+\/summary$/.test(pathname)) return loadSummary;
  if (/^\/play\/[^/]+$/.test(pathname)) return loadArgument;
  if (/^\/g\/[^/]+$/.test(pathname)) return loadPublicShare;
  return null;
}

if (typeof window !== 'undefined') {
  const load = chunkForPath(window.location.pathname);
  if (load) void load().catch(() => { /* the real navigation retries, and reloads on a stale chunk */ });
}

/**
 * What shows while a screen's chunk arrives: nothing of its own, but the room
 * it lives in, so the lift between rooms starts when the route changes (a key
 * press can skip it) and not when the chunk lands. The screen then refines the
 * shot (where in the room to land), which the stage already supports.
 */
function RoomWhileLoading() {
  const { pathname } = useLocation();
  useWorldScene({ room: roomForPath(pathname) });
  return null;
}

/** Wraps a lazy screen so its chunk can load without unmounting the shell around it. */
function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<RoomWhileLoading />}>{children}</Suspense>;
}

/**
 * Once the app is idle, fetch the chunks the next click is most likely to
 * need (landing -> case picker -> brief; the brief fetches Argument), one at a time, so
 * navigation is no slower than it was with one big bundle.
 */
function usePrefetchLikelyScreens() {
  useEffect(() => {
    // Safari has no requestIdleCallback; fall back to a timer there.
    const ric = (window as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number }).requestIdleCallback;
    let cancelled = false;
    const run = async () => {
      // The courtroom too: stepping to the lectern used to wait for its code after
      // the game was created (0.54 s on a slow phone, FEEDBACK_1).
      for (const load of [loadCasePicker, loadBriefs, loadArgument]) {
        if (cancelled) return;
        try { await load(); } catch { /* offline: the real navigation retries */ }
      }
    };
    const timer = window.setTimeout(() => {
      if (typeof ric === 'function') ric.call(window, () => { void run(); }, { timeout: 4000 });
      else void run();
    }, 1500);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, []);
}

/** The auth bootstrap: the west front while the doors open. */
function OpeningTheDoors() {
  useWorldScene({ room: 'front', dim: false });
  return (
    <main className="plaza full-screen-min-h" aria-busy="true" aria-label="SCOTUS the Nine">
      <p className="plaza-wait plaque brass" role="status">Opening the doors</p>
    </main>
  );
}

/** The auth bootstrap failed: the Court Police desk, with the reason in small type and a way to try again. */
function CouldNotSeat({ error }: { error: string | null }) {
  return (
    <main className="full-screen-min-h" aria-label="SCOTUS the Nine">
      <PoliceNotice
        title="The Court could not seat you."
        detail={error}
        actions={<button type="button" className="pb wood" onClick={() => window.location.reload()}>Try again</button>}
      >
        <p>Something went wrong while opening your seat. Trying again usually works.</p>
      </PoliceNotice>
    </main>
  );
}

/** True once the world map is in. A reading room drawn before it places its
 * papers and portraits in a fallback spot and then jumps them onto their
 * objects, which Lighthouse scored as a layout shift (desktop /court 0.118)
 * once those rooms drew before the session opened. No map is ever loaded
 * where images cannot be (tests), so there it is ready at once. */
function useWorldReady(): boolean {
  const meta = useWorldMeta();
  return meta !== null || !canLoadImages();
}

export default function App() {
  const { status, error } = useAuth();
  const worldReady = useWorldReady();
  usePrefetchLikelyScreens();
  if (status === 'loading') {
    // The west front needs no account to be drawn: only its data (an argument
    // to resume, the case count, a featured argument) waits for the session.
    // Showing it at once, rather than after the two auth round trips, was most
    // of the landing's largest-paint time on a slow phone (Lighthouse mobile,
    // 2026-10-09). When the session opens the full route tree replaces this one,
    // so the plaza mounts again once (its data fetches run then, not twice).
    return (
      <TransitionProvider>
        <MobileNotice />
        <Routes>
          <Route path="/" element={<Landing />} />
          {/* The reading rooms need no account either (no request of their
              own), so a deep link to one draws it now instead of after the
              session opens: about 0.6 s of a slow phone's largest paint. */}
          {worldReady && (
            <Route element={<Chrome />}>
              <Route path="/about" element={<Lazy><About /></Lazy>} />
              <Route path="/court" element={<Lazy><TheCourt /></Lazy>} />
              <Route path="/guide" element={<Lazy><Guide /></Lazy>} />
              <Route path="/terms" element={<Lazy><Terms /></Lazy>} />
            </Route>
          )}
          <Route path="*" element={<OpeningTheDoors />} />
        </Routes>
      </TransitionProvider>
    );
  }
  if (status === 'error') return <CouldNotSeat error={error} />;
  if (status === 'login') {
    // The site is fully public: with the invite gate off (the default) a
    // visitor never gets here, AuthContext opens an anonymous session and they
    // land on the Landing page. This branch is only for an anonymous session
    // the server declined (the per-IP rate limit) and the emergency
    // REQUIRE_INVITE gate. NoSeat asks for nothing, there is no code or email
    // sign-in in the player UI. Public share links (/g/:slug) need no account
    // at all, so they still render here.
    return (
      <TransitionProvider>
        <MobileNotice />
        <Routes>
          <Route path="/g/:slug" element={<Lazy><PublicShare /></Lazy>} />
          <Route path="*" element={<NoSeat />} />
        </Routes>
      </TransitionProvider>
    );
  }
  return (
    <TransitionProvider>
      <MobileNotice />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/g/:slug" element={<Lazy><PublicShare /></Lazy>} />
        {/* The live argument gets its own bare shell, no site nav. See Chamber.tsx. */}
        <Route element={<Chamber />}>
          <Route path="/play/:gameId" element={<Lazy><Argument /></Lazy>} />
        </Route>
        <Route element={<Chrome />}>
          <Route path="/home" element={<Lazy><CasePicker /></Lazy>} />
          <Route path="/case/:id/brief" element={<Lazy><Briefs /></Lazy>} />
          <Route path="/play/:gameId/summary" element={<Lazy><Summary /></Lazy>} />
          <Route path="/record" element={<Lazy><Record /></Lazy>} />
          <Route path="/achievements" element={<Lazy><Achievements /></Lazy>} />
          <Route path="/leaderboard" element={<Lazy><Leaderboard /></Lazy>} />
          <Route path="/settings" element={<Lazy><Settings /></Lazy>} />
          <Route path="/about" element={<Lazy><About /></Lazy>} />
          <Route path="/court" element={<Lazy><TheCourt /></Lazy>} />
          <Route path="/guide" element={<Lazy><Guide /></Lazy>} />
          <Route path="/terms" element={<Lazy><Terms /></Lazy>} />
          {/* Inside the shell, not beside it. A 404 is still a page of this
              site, and the one screen where the reader most needs the nav is
              the one they reached by mistake. */}
          <Route path="*" element={<Lazy><NotFound /></Lazy>} />
        </Route>
      </Routes>
    </TransitionProvider>
  );
}
